import React from 'react'
import type { Block, Variable } from '../../types'
import { Select, SelectTrigger, SelectContent, SelectItem, SelectValue } from '@/components/ui/select'
import { PropertyLabel, PropertyInput, ToggleSwitch, AlignPicker } from './shared'
import { AVAILABLE_ICONS } from '../../../renderer/blocks/ButtonBlock'
interface ButtonPropertiesProps {
  block: Block
  variables: Variable[]
  onUpdateBlock: (id: string, updates: Partial<Block>) => void
  onAddVariable: (name?: string) => string
}

const BUTTON_VARIANTS = [
  { value: 'primary', label: 'Primary', group: 'basic' },
  { value: 'secondary', label: 'Secondary', group: 'basic' },
  { value: 'outline', label: 'Outline', group: 'basic' },
  { value: 'ghost', label: 'Ghost', group: 'basic' },
  { value: 'pill', label: 'Pill', group: 'basic' },
  { value: 'underline', label: 'Underline', group: 'basic' },
  { value: 'glow', label: 'Glow', group: 'fun' },
  { value: '3d', label: '3D', group: 'fun' },
  { value: 'brutalist', label: 'Brutalist', group: 'fun' },
  { value: 'neon', label: 'Neon', group: 'fun' },
  { value: 'expand', label: 'Expand', group: 'fun' },
  { value: 'splash', label: 'Splash', group: 'fun' },
  { value: 'gradient', label: 'Gradient', group: 'fun' },
  { value: 'icon-only', label: 'Icon Only', group: 'fun' },
]

export default function ButtonProperties({ block, variables, onUpdateBlock, onAddVariable }: ButtonPropertiesProps) {
  const config = (block.config || {}) as Record<string, unknown>
  const variant = (config.variant as string) || 'primary'
  const href = config.href as string | undefined

  return (
    <div className="space-y-6">
      <div className="space-y-2">
        <PropertyLabel>Label</PropertyLabel>
        <PropertyInput
          value={block.label || ''}
          onChange={(e) => onUpdateBlock(block.id, { label: e.target.value })}
        />
      </div>

      {/* Alignment */}
      <div className="space-y-2">
        <PropertyLabel>Alignment</PropertyLabel>
        <AlignPicker
          value={(config.align as string) || 'left'}
          onChange={(v) => onUpdateBlock(block.id, { config: { ...config, align: v } })}
        />
      </div>

      {/* Variant picker */}
      <div className="space-y-3">
        <PropertyLabel>Style</PropertyLabel>
        <div className="space-y-1.5">
          <span className="text-[10px] text-slate-400 font-medium uppercase tracking-wider">Basic</span>
          <div className="grid grid-cols-2 gap-1.5">
            {BUTTON_VARIANTS.filter((v) => v.group === 'basic').map((v) => (
              <button
                key={v.value}
                onClick={() => onUpdateBlock(block.id, { config: { ...config, variant: v.value } })}
                className={`px-3 py-2 rounded-lg text-xs font-medium border transition-all ${
                  variant === v.value
                    ? 'border-[#5048e5] bg-[#5048e5]/10 text-[#5048e5]'
                    : 'border-slate-200 text-slate-600 hover:border-slate-300'
                }`}
              >
                {v.label}
              </button>
            ))}
          </div>
        </div>
        <div className="space-y-1.5">
          <span className="text-[10px] text-slate-400 font-medium uppercase tracking-wider">Effects</span>
          <div className="grid grid-cols-3 gap-1.5">
            {BUTTON_VARIANTS.filter((v) => v.group === 'fun').map((v) => (
              <button
                key={v.value}
                onClick={() => onUpdateBlock(block.id, { config: { ...config, variant: v.value } })}
                className={`px-2 py-2 rounded-lg text-[11px] font-medium border transition-all ${
                  variant === v.value
                    ? 'border-[#5048e5] bg-[#5048e5]/10 text-[#5048e5]'
                    : 'border-slate-200 text-slate-600 hover:border-slate-300'
                }`}
              >
                {v.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* URL (CTA mode) */}
      <div className="space-y-2">
        <PropertyLabel>Link URL (optional)</PropertyLabel>
        <PropertyInput
          value={(href as string) ?? ''}
          onChange={(e) => onUpdateBlock(block.id, { config: { ...config, href: e.target.value } })}
          placeholder="https://..."
        />
      </div>
      {href && (
        <div className="flex items-center justify-between">
          <span className="text-sm text-slate-600">Open in new tab</span>
          <ToggleSwitch
            checked={(config.newTab as boolean) ?? false}
            onChange={(v) => onUpdateBlock(block.id, { config: { ...config, newTab: v } })}
          />
        </div>
      )}

      {/* Icon picker */}
      <div className="space-y-2">
        <PropertyLabel>Icon</PropertyLabel>
        <select
          value={(config.icon as string) ?? ''}
          onChange={(e) => onUpdateBlock(block.id, { config: { ...config, icon: e.target.value || undefined } })}
          className="w-full bg-slate-50 border border-slate-200 rounded-lg p-2.5 text-sm focus:ring-2 focus:ring-[#5048e5] focus:border-transparent outline-none"
        >
          <option value="">No icon</option>
          {AVAILABLE_ICONS.map((icon) => (
            <option key={icon} value={icon}>{icon}</option>
          ))}
        </select>
      </div>

      {/* Click variable */}
      {block.variable && (
        <div className="space-y-1.5">
          <PropertyLabel>Click Variable</PropertyLabel>
          <div className="flex items-center gap-2 px-3 py-2 bg-slate-50 rounded-lg border border-slate-200">
            <span className="font-mono text-sm text-[#5048e5]">@{block.variable}</span>
            <span className="text-[10px] text-slate-400 ml-auto">true on click</span>
          </div>
        </div>
      )}

      {/* Additional action: set another variable */}
      {!href && (
        <div className="space-y-2">
          <PropertyLabel>Also Set Variable (optional)</PropertyLabel>
          <div className="flex gap-2">
            {variables.filter((v) => v.name !== block.variable).length > 0 ? (
              <div className="flex-1">
                <Select value={(config.targetVariable as string) || ''} onValueChange={(val) => onUpdateBlock(block.id, { config: { ...config, targetVariable: val || undefined } })}>
                  <SelectTrigger className="bg-slate-50 border-slate-200 rounded-lg">
                    <SelectValue placeholder="None" />
                  </SelectTrigger>
                  <SelectContent>
                    {variables.filter((v) => v.name && v.name !== block.variable).map((v) => (
                      <SelectItem key={v.name} value={v.name}>
                        @{v.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            ) : (
              <p className="flex-1 text-xs text-slate-400 italic py-2">No other variables</p>
            )}
          </div>
          {(config.targetVariable as string) && (
            <div className="space-y-2">
              <PropertyLabel>Value</PropertyLabel>
              <PropertyInput
                type="number"
                value={(config.targetValue as number) ?? 0}
                onChange={(e) => onUpdateBlock(block.id, { config: { ...config, targetValue: Number(e.target.value) } })}
              />
            </div>
          )}
        </div>
      )}
    </div>
  )
}
